import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Col
} from 'reactstrap';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  processWhatsappAssignedListGet,
  processWhatsappNotAssignedListGet,
  whatsappProcessAssign
} from '../../../store/slices/tenantSlices/configurationSlice';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { toast } from 'react-toastify';
import FormInput from '../../../components/form/FormInput';

const AssignWhatsappProcessModal = ({ isOpen, onCloseClick, process_id = null }) => {
  const dispatch = useDispatch();
  const [availableWhatsapp, setAvailableWhatsapp] = useState([]);
  const [assignedWhatsapp, setAssignedWhatsapp] = useState([]);
  const [searchAvailable, setSearchAvailable] = useState('');
  const [searchAssigned, setSearchAssigned] = useState('');
  const [filteredAvailableWhatsapp, setFilteredAvailableWhatsapp] = useState([]);
  const [filteredAssignedWhatsapp, setFilteredAssignedWhatsapp] = useState([]);

  useEffect(() => {
    if (process_id && isOpen) {
      fetchProcessWhatsappAssignedList();
      fetchProcessWhatsappNotAssignedList();
    }
  }, [process_id, isOpen]);

  const fetchProcessWhatsappNotAssignedList = async () => {
    try {
      const response = await dispatch(processWhatsappNotAssignedListGet({ process_id })).unwrap();
      if (response) {
        const newData = (response?.data || []).map((val) => {
          return {
            ...val,
            whatsapp_id: val?.id
          };
        });
        setAvailableWhatsapp(newData || []);
        setFilteredAvailableWhatsapp(newData || []);
      }
    } catch (error) {
      console.log('Error fetching available Whatsapp:', error);
    }
  };

  const fetchProcessWhatsappAssignedList = async () => {
    try {
      const response = await dispatch(processWhatsappAssignedListGet({ process_id })).unwrap();
      if (response) {
        setAssignedWhatsapp(response?.data || []);
        setFilteredAssignedWhatsapp(response?.data || []);
      }
    } catch (error) {
      console.log('Error fetching assigned Whatsapp:', error);
    }
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;

    if (!destination) return;

    const updatedAvailableWhatsapp = [...availableWhatsapp];
    const updatedAssignedWhatsapp = [...assignedWhatsapp];

    if (source.droppableId === 'available' && destination.droppableId === 'assigned') {
      const movedItem = filteredAvailableWhatsapp[source.index];
      updatedAvailableWhatsapp.splice(updatedAvailableWhatsapp.indexOf(movedItem), 1);
      updatedAssignedWhatsapp.splice(destination.index, 0, movedItem);
    }

    if (source.droppableId === 'assigned' && destination.droppableId === 'available') {
      const movedItem = filteredAssignedWhatsapp[source.index];
      updatedAssignedWhatsapp.splice(updatedAssignedWhatsapp.indexOf(movedItem), 1);
      updatedAvailableWhatsapp.splice(destination.index, 0, movedItem);
    }

    setAvailableWhatsapp(updatedAvailableWhatsapp);
    setAssignedWhatsapp(updatedAssignedWhatsapp);
    setFilteredAvailableWhatsapp(
      updatedAvailableWhatsapp.filter((item) =>
        item.whatsapp_number.toLowerCase().includes(searchAvailable.toLowerCase())
      )
    );
    setFilteredAssignedWhatsapp(
      updatedAssignedWhatsapp.filter((item) =>
        item.whatsapp_number.toLowerCase().includes(searchAssigned.toLowerCase())
      )
    );
  };

  const onSubmit = async () => {
    try {
      const queue_ids = assignedWhatsapp.map((x) => x.whatsapp_id);
      const payload = {
        whatsapp_ids: queue_ids,
        process_id: process_id
      };
      const response = await dispatch(whatsappProcessAssign(payload)).unwrap();
      toast.success(response.message);
      onCloseClick();
      setAvailableWhatsapp([]);
      setAssignedWhatsapp([]);
    } catch (error) {
      console.log('Error during queue assignment:', error);
    }
  };

  useEffect(() => {
    setFilteredAvailableWhatsapp(
      availableWhatsapp.filter((item) =>
        item.whatsapp_number.toLowerCase().includes(searchAvailable.toLowerCase())
      )
    );
  }, [searchAvailable, availableWhatsapp]);

  useEffect(() => {
    setFilteredAssignedWhatsapp(
      assignedWhatsapp.filter((item) =>
        item.whatsapp_number.toLowerCase().includes(searchAssigned.toLowerCase())
      )
    );
  }, [searchAssigned, assignedWhatsapp]);

  useEffect(() => {
    if (!isOpen) {
      setAvailableWhatsapp([]);
      setAssignedWhatsapp([]);
      setFilteredAvailableWhatsapp([]);
      setFilteredAssignedWhatsapp([]);
      setSearchAvailable('');
      setSearchAssigned('');
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">Assign Whatsapp</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <Row className="py-6">
          <DragDropContext onDragEnd={onDragEnd}>
            <Row className="py-6">
              <Col xs="6" md="6" sm="6" lg="6" className="mr-10">
                <Card>
                  <CardHeader className="bg-info py-2 text-dark text-center text-white">
                    <div>Available Whatsapp</div>
                  </CardHeader>
                  <CardBody
                    className="bg-dark-950"
                    style={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto', height: 'auto' }}>
                    <FormInput
                      placeholder="Search available Whatsapp"
                      value={searchAvailable}
                      onChange={(e) => setSearchAvailable(e.target.value)}
                      className="mb-3"
                    />
                    <Droppable droppableId="available">
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={
                            filteredAvailableWhatsapp.length === 0
                              ? 'p-2 border mb-3 bg-white rounded-2 text-primary'
                              : ''
                          }>
                          {filteredAvailableWhatsapp.length > 0 ? (
                            filteredAvailableWhatsapp.map((item, index) => (
                              <Draggable
                                key={item.whatsapp_id}
                                draggableId={String(item.whatsapp_id)}
                                index={index}>
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className="p-2 border mb-3 bg-white rounded-2 text-primary">
                                    {item.whatsapp_number || '-'}
                                  </div>
                                )}
                              </Draggable>
                            ))
                          ) : (
                            <div>No available Whatsapp</div>
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </CardBody>
                </Card>
              </Col>

              <Col xs="6" md="6" sm="6" lg="6">
                <Card>
                  <CardHeader className="bg-primary py-2 text-dark text-center text-white">
                    <div>Assigned Whatsapp</div>
                  </CardHeader>
                  <CardBody
                    className="bg-dark-950"
                    style={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto', height: 'auto' }}>
                    <FormInput
                      placeholder="Search assigned Whatsapp"
                      value={searchAssigned}
                      onChange={(e) => setSearchAssigned(e.target.value)}
                      className="mb-3"
                    />
                    <Droppable droppableId="assigned">
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={
                            filteredAssignedWhatsapp.length === 0
                              ? 'p-2 border mb-3 bg-white rounded-2 text-primary'
                              : ''
                          }>
                          {filteredAssignedWhatsapp.length > 0 ? (
                            filteredAssignedWhatsapp.map((item, index) => (
                              <Draggable
                                key={item.whatsapp_id}
                                draggableId={String(item.whatsapp_id)}
                                index={index}>
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className="p-2 border mb-3 bg-white rounded-2 text-primary">
                                    {item.whatsapp_number || '-'}
                                  </div>
                                )}
                              </Draggable>
                            ))
                          ) : (
                            <div>No assigned Whatsapp</div>
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </DragDropContext>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            setAvailableWhatsapp([]);
            setAssignedWhatsapp([]);
            setFilteredAvailableWhatsapp([]);
            setFilteredAssignedWhatsapp([]);
            setSearchAvailable('');
            setSearchAssigned('');
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button
          form="Whatsapp"
          type="submit"
          className="px-5 bg-primary border-primary"
          onClick={onSubmit}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AssignWhatsappProcessModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onCloseClick: PropTypes.func.isRequired,
  process_id: PropTypes.any
};

export default AssignWhatsappProcessModal;
